import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TicketsScope, TicketsState } from './tickets.model';
import { ListType } from './tickets.actions';
import {AppState} from "../core.state";

// Type guards
const isTicketItem = (item: any): item is TicketsScope.TicketItem => {
  return 'selections' in item && 'status' in item;
};

const isGroupTicketListItem = (item: any): item is TicketsScope.GroupTicketListItem => {
  return 'invited_count' in item && 'voted_count' in item;
};

// Feature selector
export const selectTicketState = createFeatureSelector<TicketsState>('tickets');

// Basic state selectors with memoization
export const selectTicketsList = createSelector(
  selectTicketState,
  (state: TicketsState) => state.list
);

export const selectTicket = createSelector(
  selectTicketState,
  (state: TicketsState) => state.current_ticket
);

// Keep original name for backward compatibility
export const seletctTicketLoadingError = createSelector(
  selectTicketState,
  (state: TicketsState) => state.ticketLoadError
);

// Also export correctly spelled version
export const selectTicketLoadingError = seletctTicketLoadingError;

// Memoized list item selectors
export const selectTicketListItems = createSelector(
  selectTicketsList,
  (list): TicketsScope.TicketItem[] => {
    if (!list?.items) return [];
    return list.items.filter(isTicketItem);
  }
);

export const selectGroupTicketListItems = createSelector(
  selectTicketsList,
  (list): TicketsScope.GroupTicketListItem[] => {
    if (!list?.items) return [];
    return list.items.filter(isGroupTicketListItem);
  }
);

// Memoized derived selectors
export const selectActiveTickets = createSelector(
  selectTicketListItems,
  (tickets): TicketsScope.TicketItem[] =>
    tickets.filter(ticket => ticket.is_live)
);

export const selectWinningTickets = createSelector(
  selectTicketListItems,
  (tickets): TicketsScope.TicketItem[] =>
    tickets.filter(ticket => ticket.is_live_win || ticket.is_live_consolation)
);

// Group ticket related selectors with memoization
export const selectGroupTicket = createSelector(
  selectTicketState,
  (state: TicketsState) => state.group_ticket
);

export const selectGroupTicketVotes = createSelector(
  selectGroupTicket,
  (groupTicket): TicketsScope.Vote[] => groupTicket?.votes || []
);

// Pagination and filtering with memoization
export const selectTicketFilters = createSelector(
  selectTicketState,
  (state: TicketsState) => state.pagination
);

// Status selectors with memoization
export const selectLoadingStatus = createSelector(
  selectTicketState,
  (state: TicketsState) => state.isLoading
);

export const selectIsSyndicate = createSelector(
  selectTicketState,
  (state: TicketsState) => state.isSyndicate
);

// Authentication related selectors with memoization
export const selectUnauthSecurityCode = createSelector(
  selectTicketState,
  (state: TicketsState) => state.unauthSecurityCode
);

// Composite selectors with memoization
export const selectSyndicateTickets = createSelector(
  selectTicketListItems,
  selectIsSyndicate,
  (tickets, isSyndicate): TicketsScope.TicketItem[] =>
    isSyndicate ? tickets.filter(ticket => ticket.syndicate_id !== undefined) : []
);


// Unified list selectors
export const selectTicketsState = (state: AppState) => state.tickets;

export const selectListState = (listType: ListType) => createSelector(
  selectTicketsState,
  (state: TicketsState) => state.lists[listType]
);

export const selectListItems = (listType: ListType) => createSelector(
  selectListState(listType),
  (state) => ({
    items: state.items,
    total_items: state.totalItems
  })
);

export const selectListPagination = (listType: ListType) => createSelector(
  selectListState(listType),
  (state) => state.pagination
);

export const selectListLoading = (listType: ListType) => createSelector(
  selectListState(listType),
  (state) => state.isLoading
);
